import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useLoginPasskey } from "../hooks/usePasskey";

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") ?? "/";
  const loginPasskey = useLoginPasskey();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePasskey = () => {
    setError(null);
    loginPasskey.mutate(undefined, {
      onSuccess: () => navigate(returnTo, { replace: true }),
      onError: (err) => {
        if (err instanceof ApiError) {
          const code = (err.payload as { code?: string } | undefined)?.code;
          setError(
            code === "NO_PASSKEY"
              ? "Nenhum passkey registrado ainda. Entre por email e registre em Ajustes."
              : `Erro ${err.status}`
          );
        } else if ((err as Error).name === "NotAllowedError") {
          setError("Autenticação cancelada.");
        } else {
          setError((err as Error).message);
        }
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.auth.requestLink(email.trim());
      setSent(true);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? typeof err.payload === "object" && err.payload && "error" in err.payload
            ? String((err.payload as any).error)
            : `Erro ${err.status}`
          : (err as Error).message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-sm mx-auto mt-16 text-center">
        <h1 className="text-2xl font-semibold mb-3">Verifique seu email</h1>
        <p className="text-sm text-slate-600">
          Mandamos um link de acesso pra <span className="font-medium">{email}</span>.<br />
          Clique no link em até 5 minutos pra entrar.
        </p>
        <button
          onClick={() => {
            setSent(false);
            setEmail("");
          }}
          className="mt-6 text-sm text-slate-500 hover:text-slate-900 underline"
        >
          Usar outro email
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto mt-16">
      <h1 className="text-2xl font-semibold mb-2">jp-voice</h1>
      <p className="text-sm text-slate-600 mb-6">Entre com seu passkey (Face ID / Touch ID).</p>

      <button
        type="button"
        data-testid="login-passkey"
        onClick={handlePasskey}
        disabled={loginPasskey.isPending}
        className="w-full rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
      >
        {loginPasskey.isPending ? "Autenticando..." : "Entrar com Face ID"}
      </button>

      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200 dark:bg-zinc-700" />
        ou por email
        <span className="h-px flex-1 bg-slate-200 dark:bg-zinc-700" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@dominio.com"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
        />
        <button
          type="submit"
          disabled={loading || !email}
          className="w-full rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? "Enviando..." : "Enviar link de acesso"}
        </button>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </form>
    </div>
  );
};

export default Login;
